/**
 * LocationView - Web implementation matching mobile's LocationView.tsx
 * Renders a location display that can be either editable or non-editable.
 */

import { Icon } from "@/components/ui/Icon";
import { StaticMapPreview } from "./StaticMapPreview";
import type { GeocodingFeature } from "@tinyclub/shared/models";

export interface LocationViewProps {
  editable?: boolean;
  location?: GeocodingFeature | null | undefined;
  locationName?: string | null | undefined;
  emptyStateTitle?: string;
  onLocationClick?: () => void; // Opens location picker
  onClearLocation?: () => void; // Clears location
}

/**
 * Renders a location display that can be either editable or non-editable.
 * Matches mobile's LocationView behavior.
 */
export function LocationView({
  editable = false,
  location,
  locationName,
  emptyStateTitle = "Add location",
  onLocationClick,
  onClearLocation,
}: LocationViewProps) {
  const hasLocation = Boolean(location);
  const hasCustomName = Boolean(locationName) && locationName !== "None";

  // Pull the coordinates
  const coords = location?.geometry?.coordinates; // [lon, lat] | undefined
  const lat = coords?.[1];
  const lon = coords?.[0];

  // Fallback text from location
  const fallbackName =
    location?.properties?.name_preferred || location?.properties?.name;
  const address =
    location?.properties?.full_address || location?.properties?.place_formatted;

  // Determine display logic
  const isStreetAddress = location?.properties?.feature_type?.includes("address");
  const isCityLevel = location?.properties?.feature_type === "place";
  const showTopLine = hasCustomName && !isStreetAddress;
  // Zoom out for city-level locations
  const mapZoomLevel = isCityLevel ? 12 : 14;
  const topLine = fallbackName;
  const secondLine = address || "";

  // For non-editable mode, pressing opens directions
  const handleOpenDirections = () => {
    if (!lat || !lon) return;

    // Build Google Maps directions URL (same pattern as mobile)
    const destination = `${lat},${lon}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, "_blank");
  };

  if (editable) {
    // EDITABLE STATE
    if (!hasLocation) {
      // Empty state: icon + "Add location" text + chevron
      return (
        <button
          type="button"
          onClick={onLocationClick}
          className="w-full flex items-center justify-between p-4 rounded-2xl bg-surface text-left hover:bg-surface-1 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Icon name="location-dot" size={16} className="text-text-primary" />
            <span className="text-body-2-medium text-text-secondary">
              {emptyStateTitle}
            </span>
          </div>
          <Icon name="angle-right" size={17} className="text-text-tertiary" />
        </button>
      );
    }

    // Has location: show map preview with location details
    return (
      <div className="w-full p-3 rounded-2xl bg-surface">
        {/* Header row */}
        <button
          type="button"
          onClick={onLocationClick}
          className="w-full flex items-center justify-between text-left"
        >
          <span className="text-body-1 text-text-primary">Location</span>
          <div className="flex-1 flex items-center justify-end gap-2 ml-4">
            <span className="text-body-1 text-text-secondary truncate max-w-[200px]">
              {hasCustomName ? locationName : fallbackName}
            </span>
            {onClearLocation ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearLocation();
                }}
                className="p-1 text-text-tertiary hover:text-text-secondary"
              >
                <Icon name="circle-xmark" size={16} />
              </button>
            ) : (
              <Icon
                name="angle-right"
                size={18}
                className="text-text-tertiary flex-shrink-0"
              />
            )}
          </div>
        </button>

        {/* Static Map Preview */}
        {lat !== undefined && lon !== undefined && (
          <div className="my-3">
            <StaticMapPreview
              lat={lat}
              lon={lon}
              zoom={mapZoomLevel}
              className="h-[120px] rounded"
            />
          </div>
        )}

        {/* Location details */}
        {showTopLine && topLine && (
          <p className="text-body-2 text-text-primary">{topLine}</p>
        )}
        {secondLine && (
          <p className="text-body-2 text-text-secondary">{secondLine}</p>
        )}
      </div>
    );
  }

  // NON-EDITABLE STATE
  if (!hasLocation) {
    // No location => nothing to show
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleOpenDirections}
      className="w-full rounded-2xl bg-surface-1 text-left overflow-hidden"
    >
      <div className="pt-3 px-3 pb-3">
        <p className="text-body-1-medium text-text-primary mb-2">
          {locationName || topLine}
        </p>
        {hasCustomName && !isStreetAddress && topLine && (
          <p className="text-body-1 text-text-secondary">{topLine}</p>
        )}
        {secondLine && (
          <p className="text-body-1 text-text-secondary">{secondLine}</p>
        )}
        {/* Get directions link */}
        <div className="mt-3">
          <span className="text-body-2-medium text-accent-secondary">
            Get directions
          </span>
        </div>
      </div>
      {lat !== undefined && lon !== undefined && (
        <StaticMapPreview
          lat={lat}
          lon={lon}
          zoom={mapZoomLevel}
          height={154}
          className="rounded-b-2xl"
        />
      )}
    </button>
  );
}
